import {
  MouseEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {LongTap} from '../../../../../../design-system/atoms/LongTap/LongTap';
import {TalkAudio} from '../../../../../../design-system/organisms/TalkAudio/TalkAudio';
import {TalkEffect} from '../../../../../../design-system/organisms/TalkEffect/TalkEffect';
import {TalkEmoticon} from '../../../../../../design-system/organisms/TalkEmoticon/TalkEmoticon';
import {TalkImage} from '../../../../../../design-system/organisms/TalkImage/TalkImage';
import {TalkProfile} from '../../../../../../design-system/organisms/TalkProfile/TalkProfile';
import {TalkReplayBox} from '../../../../../../design-system/organisms/TalkReplayBox/TalkReplayBox';
import {TalkText} from '../../../../../../design-system/organisms/TalkText/TalkText';
import {TalkVideo} from '../../../../../../design-system/organisms/TalkVideo/TalkVideo';
import {events} from '../constant/talkViewerEvents';
import {pauseMedia, playMedia, stopMedia} from '../state/talkViewerSlice';
import {CharacterShowType} from '../type/CharacterShowType.enum';
import {FontLoadStatus} from '../type/FontLoadStatus.enum';
import {TalkKey} from '../type/KeyType.type';
import {TalkDataType} from '../type/TalkDataType.enum';
import {TalkMediaStatus} from '../type/TalkMediaStatus.enum';
import {TalkPosition} from '../type/TalkPosition.enum';
import {TalkShapeType} from '../type/TalkShapeType.enum';
import {TalkType} from '../type/TalkType.enum';
import {VideoPlayType} from '../type/VIdeoPlayType.enum';
import {ZIndex} from '../type/ZIndex.enum';
import {getDataUrlOrUrl} from '../util/getDataUrlOrUrl';

interface TalkViewerTalkContainerProps {
  talkId: TalkKey;
  setTalkEl: (el: HTMLDivElement, talkId: string) => void;
  isShowingTalk: boolean;
}

export const TalkViewerTalkContainer = memo(
  ({talkId, setTalkEl, isShowingTalk}: TalkViewerTalkContainerProps) => {
    const {
      talk,
      talkInfo,
      preloadedMedia,
      muted,
      videoPlayType,
      needMediaPlayedDirect,
      fontLoadStatus,
    } = useTypedSelector(state => {
      return {
        talk: state.talkViewer.talkMap[talkId],
        talkInfo: state.talkViewer.talkInfoMap[talkId],
        preloadedMedia: state.talkViewer.preloadedMedia,
        muted: state.talkViewer.internalSettings.muted,
        videoPlayType: state.talkViewer.videoPlayType,
        needMediaPlayedDirect: state.talkViewer.needMediaPlayedDirect,
        fontLoadStatus: state.talkViewer.fontLoadStatus,
      };
    });

    const talkElementRef = useRef<HTMLDivElement>();
    const {
      talkType,
      shapeType,
      datatype,
      background,
      characterInfo,
      font,
      media,
      position,
      scripts,
      characterId,
      effect,
      align,
    } = talk;
    // const { color } = font;
    const character = useTypedSelector(state =>
      characterId ? state.talkViewer.characterMap[characterId] : undefined
    );

    const isCurrentTalkFullscreenVideo = useTypedSelector(
      state =>
        state.talkViewer.fullscreen?.talkId === talkId &&
        (datatype === TalkDataType.FullscreenVideo ||
          datatype === TalkDataType.Movie)
    );

    // showingTalk의 변화에 바로 톡을 보여주면 이전보다 렌더가 빨라져서 잔상이 남는다. 그래서 isShowingTalk 변경에 따라 한 번 rerender 후 보일 수 있도록 state를 가지고 있는다.
    const [isVisibleTalk, setIsVisibleTalk] = useState(false);
    useEffect(() => {
      setIsVisibleTalk(isShowingTalk);
    }, [isShowingTalk]);

    const dispatch = useTypedDispatch();
    const onClickTalk = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        dispatch(events.talkTapPressed(talkId));
      },
      [talkId, dispatch]
    );

    const onLongClickTalk = useCallback(() => {
      dispatch(events.talkLongTapPressed(talkId));
    }, [talkId, dispatch]);

    const onClickReplayBox = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        dispatch(events.replayBoxTapPressed(talkId));
      },
      [talkId, dispatch]
    );

    const isShowProfile = !!(
      talkType === TalkType.Talker &&
      characterInfo?.showType &&
      shapeType === TalkShapeType.Bubble
    );

    // 이 파일 내의 innerTalkRef와 외부 TalkList ref에 현재 톡 element를 세팅해준다.
    const setTalkRef = useCallback(
      (talkElement: HTMLDivElement | null) => {
        if (talkElement) {
          talkElementRef.current = talkElement;
          setTalkEl(talkElement, talkId);
        }
      },
      [setTalkEl, talkId]
    );

    const onClickCloseFullscreen = useCallback(() => {
      dispatch(events.fullscreenHideRequested());
    }, [dispatch]);

    const onClickToggleMuted = useCallback(() => {
      dispatch(events.mutedToggleTapPresssed());
    }, [dispatch]);

    const changeVideoMediaStatus = useCallback(
      (status: TalkMediaStatus) => {
        if (status === TalkMediaStatus.Play) {
          dispatch(playMedia(talkId));
        } else if (status === TalkMediaStatus.Pause) {
          dispatch(pauseMedia(talkId));
        } else if (status === TalkMediaStatus.Stop) {
          dispatch(stopMedia(talkId));
        }
      },
      [dispatch, talkId]
    );

    const onMediaEnded = useCallback(() => {
      dispatch(events.mediaEnded(talkId));
    }, [dispatch, talkId]);

    // 현재 풀스크린이 떠 있는 이미지일 경우 톡을 보여주지 않는다. 애니메이션이 들어가면서 추가된 부분
    // 배경음 톡인 경우에도 톡을 보여주지 않는다
    const isHideTalk = datatype === TalkDataType.Bgm;
    if (datatype === TalkDataType.None) {
      return (
        <div
          ref={setTalkRef}
          className={twMerge('invisible h-0', !isShowingTalk && 'hidden')}
        />
      );
    }

    const isVideoFullscreenOnWeb =
      isCurrentTalkFullscreenVideo && videoPlayType === VideoPlayType.Web;

    const hasTalkTail =
      characterInfo?.showType === CharacterShowType.Normal ||
      characterInfo?.showType === CharacterShowType.NameOnly;
    const {isIos, isMobile, isKakaotalk} = requestContext.requestInfo.display;
    return (
      <div
        className={twMerge(
          'invisible relative flex items-start px-20tvs',
          (isHideTalk || !isShowingTalk) && 'hidden',
          isVisibleTalk &&
            fontLoadStatus !== FontLoadStatus.BeforeLoad &&
            'visible',
          shapeType !== TalkShapeType.Box && [
            BUTTLE_TALK_TYPE_STYLE[talkType],
            characterInfo?.showType === CharacterShowType.SpaceOnly &&
              'mt-20tvs-minus',
            TALK_POSITION_STYLE[position],
          ],
          shapeType === TalkShapeType.Box && 'my-5tvs justify-center',
          isVideoFullscreenOnWeb &&
            'fixed top-0 left-0 m-0 h-full max-h-full w-full max-w-full border-none p-0'
        )}
        style={{
          transform: 'translate3d(0, 0, 0)',
          zIndex: isVideoFullscreenOnWeb
            ? ZIndex.FullScreen
            : talkInfo.isShowReplay
              ? ZIndex.Talk + 1
              : ZIndex.Talk,
        }}
      >
        {isShowProfile && position === TalkPosition.Left && (
          <TalkProfile
            characterName={character?.name}
            talkPosition={position}
            characterImgUrl={
              character?.image
                ? getDataUrlOrUrl(character.image, preloadedMedia)
                : undefined
            }
            characterShowType={characterInfo.showType}
            className={twMerge('mr-10tvs', isVideoFullscreenOnWeb && 'hidden')}
            characterNameColor={characterInfo.nameColor}
          />
        )}
        <LongTap
          isMobile={isMobile}
          onClick={onClickTalk}
          onLongTap={onLongClickTalk}
          longTapTimer={1000}
          ref={setTalkRef}
          className={twMerge(
            'relative',
            isShowProfile &&
              (characterInfo.showType === CharacterShowType.Normal ||
                characterInfo.showType === CharacterShowType.NameOnly) &&
              'mt-45tvs',
            isVideoFullscreenOnWeb && 'm-0 w-full'
          )}
          style={{
            transform: 'translate3d(0, 0, 0)',
          }}
        >
          {datatype === TalkDataType.Talk && (
            <TalkText
              text={scripts}
              textAlign={align}
              talkShapeType={shapeType}
              talkType={talkType}
              isShowingTalk={isShowingTalk}
              fontLoadStatus={fontLoadStatus}
              talkColor={background.color}
              talkBorderColor={background.border}
              textColor={font.color}
              characterName={character?.name}
              characterNameColor={characterInfo?.nameColor}
              talkPosition={position}
              hasTalkTail={hasTalkTail}
            />
          )}
          {(datatype === TalkDataType.Image ||
            datatype === TalkDataType.FullscreenImage) &&
            media && (
              <TalkImage
                isIos={isIos}
                imageUrl={getDataUrlOrUrl(media.source.url, preloadedMedia)}
                apngUrl={getDataUrlOrUrl(
                  media.source.url.replace('wepb', 'apng'),
                  preloadedMedia
                )}
                widthPx={media.source.width}
                heightPx={media.source.height}
                alt={media.source.alt}
                authorship={media.source.authorship}
                talkPosition={position}
                talkType={talkType}
                talkShapeType={shapeType}
                characterName={character?.name}
                characterNameColor={characterInfo?.nameColor}
                talkColor={background.color}
                isFullscreenImg={datatype === TalkDataType.FullscreenImage}
              />
            )}
          {datatype === TalkDataType.Emoticon && media && (
            <TalkEmoticon
              isIos={isIos}
              isShowingTalk={isShowingTalk}
              talkShapeType={shapeType}
              characterName={character?.name}
              characterNameColor={characterInfo?.nameColor}
              talkColor={background.color}
              talkPosition={position}
              emoticonWebpUrl={getDataUrlOrUrl(
                media.source.url,
                preloadedMedia
              )}
              emoticonApngUrl={getDataUrlOrUrl(
                media.source.url.replace('wepb', 'apng'),
                preloadedMedia
              )}
            />
          )}
          {(datatype === TalkDataType.Movie ||
            datatype === TalkDataType.FullscreenVideo) &&
            media && (
              <TalkVideo
                media={media}
                mediaStatus={talkInfo && talkInfo.mediaStatus}
                preloadedMedia={preloadedMedia}
                isFullscreen={isVideoFullscreenOnWeb}
                muted={muted}
                isMobile={isMobile}
                videoPlayType={videoPlayType}
                isKatalk={isKakaotalk}
                needMediaPlayedDirect={needMediaPlayedDirect}
                talkShapeType={shapeType}
                characterName={character?.name}
                characterNameColor={characterInfo?.nameColor}
                talkColor={background.color}
                talkPosition={position}
                onClickCloseFullscreen={onClickCloseFullscreen}
                toggleMuted={onClickToggleMuted}
                onVideoEnded={onMediaEnded}
                changeVideoMediaStatus={changeVideoMediaStatus}
                talkType={talkType}
              />
            )}
          {datatype === TalkDataType.Sound && media && (
            <TalkAudio
              textColor={font.color}
              media={media}
              mediaStatus={talkInfo && talkInfo.mediaStatus}
              showEqualizer={talk.datatype === TalkDataType.Sound}
              muted={muted}
              needMediaPlayedDirect={needMediaPlayedDirect}
              talkShapeType={shapeType}
              characterName={character?.name}
              characterNameColor={characterInfo?.nameColor}
              talkColor={background.color}
              talkPosition={position}
              talkType={talkType}
              onEndedAudio={onMediaEnded}
              talkBorderColor={background.border}
              hasTalkTail={hasTalkTail}
            />
          )}
          {datatype === TalkDataType.TalkEffect && (
            <TalkEffect
              effectType={effect}
              scripts={scripts}
              font={font}
              audioUrl={media?.source?.url}
              mediaStatus={talkInfo && talkInfo.mediaStatus}
              muted={muted}
              isShowingTalk={isShowingTalk}
              needMediaPlayedDirect={needMediaPlayedDirect}
              onAudioEnded={onMediaEnded}
              fontLoadStatus={fontLoadStatus}
            />
          )}

          <TalkReplayBox
            onClickReplayBox={onClickReplayBox}
            talkPosition={position}
            talkWidth={talkElementRef.current?.clientWidth ?? 0}
            showReplay={talkInfo && talkInfo.isShowReplay}
          />
        </LongTap>

        {isShowProfile && position === TalkPosition.Right && (
          <TalkProfile
            characterName={character?.name}
            talkPosition={position}
            characterImgUrl={character?.image}
            characterShowType={characterInfo.showType}
            className={twMerge('ml-10tvs', isVideoFullscreenOnWeb && 'hidden')}
            characterNameColor={characterInfo.nameColor}
          />
        )}
      </div>
    );
  }
);

const TALK_POSITION_STYLE: {[key in TalkPosition]: string} = {
  [TalkPosition.Center]: 'justify-center',
  [TalkPosition.Left]: 'justify-start',
  [TalkPosition.Right]: 'justify-end',
};

const BUTTLE_TALK_TYPE_STYLE: {[key in TalkType]: string} = {
  [TalkType.Narration]: 'my-50tvs',
  [TalkType.Omniscient]: 'my-25tvs',
  [TalkType.Talker]: 'my-30tvs',
  [TalkType.None]: 'my-25tvs',
};
